import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, toggleUserStatus, updateUser } from '../../Redux/adminSlice';
import ContentLoader from '../utils/ContentLoader';
import adminService from '../services/adminService';
import { exportToPDF, exportToExcel } from '../utils/exportUtils';
import { UserTable, UserModal, UsersFilter } from '../components/users';

const defaultFilters = {
  profileComplete: '',
  contractStatus: '',
  contentType: [],
  followersMin: '',
  followersMax: '',
};

const UsersManagement = () => {
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(defaultFilters);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [exportLoading, setExportLoading] = useState(false);
  const [contractStatusSaving, setContractStatusSaving] = useState(false);

  const dispatch = useDispatch();
  const { data: users, loading, error, pagination } = useSelector((state) => state.admin.users);

  const loadUsers = useCallback(
    (page = currentPage, searchTerm = search, filterValues = filters) => {
      dispatch(fetchUsers({ page, limit: 10, search: searchTerm, filters: filterValues }));
    },
    [dispatch, currentPage, search, filters]
  );

  useEffect(() => {
    loadUsers(currentPage, search, filters);
  }, [currentPage]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    dispatch(fetchUsers({ page: 1, limit: 10, search, filters }));
  };

  const handleRefresh = () => {
    dispatch(fetchUsers({ page: currentPage, limit: 10, search, filters }));
  };

  const handleClearSearch = useCallback(() => {
    setSearch('');
    setCurrentPage(1);
    dispatch(fetchUsers({ page: 1, limit: 10, search: '', filters }));
  }, [dispatch, filters]);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    dispatch(fetchUsers({ page: 1, limit: 10, search, filters: newFilters }));
  }, [dispatch, search]);

  const handleFiltersClear = useCallback(() => {
    setFilters(defaultFilters);
    setCurrentPage(1);
    dispatch(fetchUsers({ page: 1, limit: 10, search, filters: defaultFilters }));
  }, [dispatch, search]);

  const handleStatusChange = useCallback(
    (userId, newStatus) => {
      dispatch(toggleUserStatus({ userId, status: newStatus }))
        .unwrap()
        .then(() => {
          if (selectedUser && selectedUser._id === userId) {
            setSelectedUser((prev) => (prev ? { ...prev, status: newStatus } : null));
          }
        })
        .catch((err) => console.error('Failed to update status:', err));
    },
    [dispatch, selectedUser?._id]
  );

  const openModal = useCallback(async (user, mode = 'view') => {
    setModalMode(mode);
    setShowModal(true);
    setSelectedUser(user);
    try {
      const response = await adminService.getUserDetails(user._id);
      if (response.success && response.data) {
        setSelectedUser(response.data);
      }
    } catch (err) {
      console.error('Failed to load user details:', err);
    }
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedUser(null);
  }, []);

  const handleContractStatusSave = useCallback(
    async (payload) => {
      if (!selectedUser?._id) return;
      setContractStatusSaving(true);
      try {
        const result = await dispatch(
          updateUser({ userId: selectedUser._id, payload })
        ).unwrap();
        if (result?.data) {
          setSelectedUser(result.data);
        }
      } finally {
        setContractStatusSaving(false);
      }
    },
    [dispatch, selectedUser?._id]
  );

  const handleExportPDF = async () => {
    try {
      setExportLoading(true);
      const response = await adminService.exportUsers();
      if (response.success && response.data) {
        await exportToPDF(response.data, 'users_export');
      }
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExportLoading(true);
      const response = await adminService.exportUsers();
      if (response.success && response.data) {
        exportToExcel(response.data, 'users_export');
      }
    } catch (err) {
      console.error('Error exporting Excel:', err);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const paginationProps =
    pagination && pagination.pages > 1
      ? {
          ...pagination,
          onPrevious: () => setCurrentPage((p) => Math.max(1, p - 1)),
          onNext: () => setCurrentPage((p) => Math.min(pagination.pages, p + 1)),
        }
      : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Influencer Management</h1>
      </div>

      {loading && <ContentLoader message="Loading users..." />}

      {!loading && error && (
        <div className="bg-red-50 text-red-500 p-4 rounded-md">
          <h3 className="text-lg font-medium">Error loading users</h3>
          <p>{error}</p>
          <button
            type="button"
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-4">
          <UsersFilter
            filters={filters}
            onChange={handleFiltersChange}
            onClear={handleFiltersClear}
          />
          <UserTable
          users={users}
          loading={loading}
          pagination={paginationProps}
          search={search}
          onSearchChange={setSearch}
          onSearchSubmit={handleSearch}
          onClearSearch={handleClearSearch}
          onRefresh={handleRefresh}
          onExportPDF={handleExportPDF}
          onExportExcel={handleExportExcel}
          exportLoading={exportLoading}
          onViewUser={(user) => openModal(user, 'view')}
          onEditUser={(user) => openModal(user, 'edit')}
        />
        </div>
      )}

      {showModal && selectedUser && (
        <UserModal
          user={selectedUser}
          mode={modalMode}
          onClose={closeModal}
          onStatusChange={handleStatusChange}
          onContractStatusSave={handleContractStatusSave}
          contractStatusSaving={contractStatusSaving}
        />
      )}
    </div>
  );
};

export default UsersManagement;
